import { position, autoUpdate } from '../floating.js';
import { onOutsidePointer } from '../listbox.js';
import { toIso, parseIso, today, addDays, addMonths, startOfMonth, formatDate } from '../dates.js';

/** Preset ranges, relative to today. */
const PRESETS = {
    today: () => [today(), today()],
    yesterday: () => [addDays(today(), -1), addDays(today(), -1)],
    last7Days: () => [addDays(today(), -6), today()],
    last30Days: () => [addDays(today(), -29), today()],
    thisMonth: () => [startOfMonth(today()), today()],
    lastMonth: () => {
        const start = startOfMonth(addMonths(today(), -1));
        return [start, addDays(startOfMonth(today()), -1)];
    },
    thisYear: () => [new Date(today().getFullYear(), 0, 1, 12), today()],
};

/**
 * <mellow-date-picker>: a trigger showing the formatted date(s) and a <mellow-calendar> in
 * a popover. `.value` mirrors the calendar value; changes fire `input` for wire:model.
 */
export class MellowDatePicker extends HTMLElement {
    connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        // The calendar's own events are re-dispatched by the picker.
        this.addEventListener('input', this.onInner, true);
        this.addEventListener('change', this.onInner, true);

        this.addEventListener('mellow:select', (event) => {
            if (event.target !== this.calendar) return;
            if (event.detail.complete && this.calendar.mode !== 'multiple') this.close();
        });

        this.trigger?.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
        this.trigger?.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.open();
            }
        });

        this.panel?.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                this.close();
            }
        });
        this.panel?.addEventListener('toggle', (event) => {
            if (event.newState === 'closed') {
                this.trigger?.setAttribute('aria-expanded', 'false');
                this._cleanup?.();
                this._outside?.();
            }
        });

        this.querySelector('[data-mellow-date-clear]')?.addEventListener('click', () => {
            this.value = this.calendar?.mode === 'multiple' ? [] : this.calendar?.mode === 'range' ? { start: null, end: null } : '';
            this.emit();
            this.trigger?.focus();
        });

        this.querySelectorAll('[data-preset]').forEach((button) => {
            button.addEventListener('click', () => {
                const [start, end] = PRESETS[button.dataset.preset]?.() ?? [];
                if (!start) return;
                this.value = this.calendar.mode === 'range' ? { start: toIso(start), end: toIso(end) } : toIso(start);
                this.emit();
                this.close();
            });
        });

        this.render();
    }

    /** Livewire morphs reset the server-rendered trigger text: render it again. */
    sync() {
        this.render();
    }

    get trigger() {
        return this.querySelector('.mellow-date-picker-trigger');
    }

    get panel() {
        return this.querySelector(':scope > [popover]');
    }

    get calendar() {
        return this.querySelector('mellow-calendar');
    }

    get isOpen() {
        return this.panel?.matches(':popover-open') ?? false;
    }

    get value() {
        return this.calendar?.value ?? '';
    }

    set value(value) {
        if (this.calendar) this.calendar.value = value;
        this.render();
    }

    onInner = (event) => {
        // Only the calendar's value events are ours to re-emit; others (e.g. its header dropdowns) handle themselves.
        if (event.target !== this.calendar) return;
        event.stopPropagation();
        this.render();
        this.emit(event.type);
    };

    emit(type) {
        const value = this.value;
        for (const name of type ? [type] : ['input', 'change']) {
            this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: value }));
        }
    }

    /** Show the formatted selection in the trigger. */
    render() {
        const display = this.querySelector('.mellow-listbox-value');
        const calendar = this.calendar;
        if (!display || !calendar) return;

        const locale = calendar.locale;
        const value = calendar.value;
        let text = '';

        if (calendar.mode === 'range') {
            const start = parseIso(value.start);
            const end = parseIso(value.end);
            if (start) text = end ? `${formatDate(start, locale)} – ${formatDate(end, locale)}` : `${formatDate(start, locale)} – …`;
        } else if (calendar.mode === 'multiple') {
            text = value.length === 1 ? formatDate(parseIso(value[0]), locale) : value.length ? (this.dataset.countLabel ?? ':count').replace(':count', value.length) : '';
        } else {
            text = formatDate(parseIso(value), locale);
        }

        const empty = !text;
        const shown = empty ? this.dataset.placeholder : text;
        if (display.textContent !== shown) display.textContent = shown;
        display.toggleAttribute('data-empty', empty);

        const clear = this.querySelector('[data-mellow-date-clear]');
        if (clear) clear.hidden = empty;

        // Plain forms: hidden inputs carry the ISO value(s).
        this.querySelectorAll('[data-mellow-date-value]').forEach((input) => {
            const part = input.dataset.mellowDateValue;
            const next = part ? (value?.[part] ?? '') : Array.isArray(value) ? value.join(',') : (value ?? '');
            if (input.value !== next) input.value = next;
        });
    }

    open() {
        if (this.isOpen || this.trigger?.disabled) return;
        this.panel.showPopover();
        this.trigger.setAttribute('aria-expanded', 'true');
        this._cleanup = autoUpdate(this.trigger, this.panel, () => position(this.trigger, this.panel, { placement: 'bottom', align: 'start', offset: 4 }));
        this._outside = onOutsidePointer(this, () => this.close(false));
        this.calendar?.focusDay();
    }

    close(focusTrigger = true) {
        if (!this.isOpen) return;
        this.panel.hidePopover();
        if (focusTrigger) this.trigger?.focus();
    }
}
